import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { publicLeadsApi, type PublicLeadLink, type PublicLeadField } from '@/services/api';
import { LoadingSpinner } from '@/components/common';
import { LeadPublicShell } from '@/components/crm/LeadPublicShell';
import { leadInputClass, leadLabelClass } from '@/components/crm/styles';

const errMsg = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error || fallback;

function FieldInput({ field, value, onChange }: {
  field: PublicLeadField; value: string | boolean; onChange: (v: string | boolean) => void;
}) {
  const id = `lead-${field.key.replace(/\./g, '-')}`;
  if (field.type === 'boolean') {
    return (
      <label htmlFor={id} className="flex items-center gap-2 text-sm text-gray-800">
        <input id={id} type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
        {field.label}
      </label>
    );
  }
  return (
    <div>
      <label htmlFor={id} className={leadLabelClass}>{field.label}</label>
      {field.type === 'textarea' ? (
        <textarea id={id} rows={4} maxLength={field.max} className={leadInputClass}
          value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} />
      ) : field.type === 'color' ? (
        <div className="flex gap-2">
          <input type="color" aria-label={`${field.label}: selector`} className="h-10 w-14 rounded border border-gray-300"
            value={/^#[0-9a-f]{6}$/i.test(String(value)) ? String(value) : '#000000'} onChange={(e) => onChange(e.target.value)} />
          <input id={id} className={leadInputClass} placeholder="#rrggbb" value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} />
        </div>
      ) : (
        <input id={id} type={field.type === 'url' ? 'url' : 'text'} placeholder={field.type === 'url' ? 'https://' : undefined}
          maxLength={field.max} className={leadInputClass} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

/** Formulario del enlace personal del lead (ALTA LEAD 1.3). Ruta pública /lead/:orgSlug/:token. */
export default function LeadLinkFormPage() {
  const { orgSlug = '', token = '' } = useParams();
  const [data, setData] = useState<PublicLeadLink | null>(null);
  const [loadError, setLoadError] = useState('');
  const [values, setValues] = useState<Record<string, string | boolean>>({});
  const [fax, setFax] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    publicLeadsApi.getLink(orgSlug, token)
      .then((r) => {
        setData(r.data);
        setValues(Object.fromEntries(r.data.fields.map((f) => [f.key, f.value])));
      })
      .catch((e) => {
        const status = (e as { response?: { status?: number } })?.response?.status;
        setLoadError(status === 410 ? 'Este enlace ha caducado. Pide uno nuevo a quien te lo envió.' : 'Este enlace no es válido.');
      });
  }, [orgSlug, token]);

  const groups = useMemo(() => {
    const map = new Map<string, PublicLeadField[]>();
    for (const f of data?.fields || []) map.set(f.group, [...(map.get(f.group) || []), f]);
    return [...map.entries()];
  }, [data]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await publicLeadsApi.submitLink(orgSlug, token, values, fax || undefined);
      setDone(true);
    } catch (err) { setError(errMsg(err, 'No se pudo guardar. Prueba de nuevo.')); } finally { setBusy(false); }
  };

  if (loadError) return <LeadPublicShell org={null}><p className="text-gray-700">{loadError}</p></LeadPublicShell>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><LoadingSpinner /></div>;

  const until = new Date(data.expiresAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });

  if (done) {
    return (
      <LeadPublicShell org={data.org}>
        <div className="flex flex-col items-center text-center gap-3 py-6">
          <CheckCircleIcon className="w-12 h-12 text-vida-600" aria-hidden="true" />
          <h1 className="text-xl font-semibold text-gray-900">Datos guardados. Gracias.</h1>
          <p className="text-gray-600">Puedes volver a este enlace para revisarlos hasta el {until}.</p>
          <button onClick={() => setDone(false)} className="text-sm font-medium text-vida-700 hover:underline">Revisar mis datos</button>
        </div>
      </LeadPublicShell>
    );
  }

  return (
    <LeadPublicShell org={data.org}>
      <h1 className="text-xl font-semibold text-gray-900">
        {data.greetingName ? `Hola, ${data.greetingName}` : 'Completa tus datos'}
      </h1>
      <p className="text-sm text-gray-600 mt-1">
        {data.projectName ? `Para preparar la propuesta de «${data.projectName}» necesitamos estos datos.` : 'Necesitamos estos datos para preparar tu propuesta.'}
        {' '}Puedes volver a este enlace hasta el {until}.
      </p>
      <form onSubmit={submit} className="mt-6 space-y-6">
        {groups.map(([group, fields]) => (
          <fieldset key={group} className="space-y-4">
            <legend className="text-sm font-semibold text-gray-900 mb-2">{group}</legend>
            {fields.map((f) => (
              <FieldInput key={f.key} field={f} value={values[f.key] ?? ''} onChange={(v) => setValues({ ...values, [f.key]: v })} />
            ))}
          </fieldset>
        ))}
        <div className="absolute left-[-10000px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
          <label htmlFor="lead-link-fax">Fax</label>
          <input id="lead-link-fax" name="fax" tabIndex={-1} autoComplete="off" value={fax} onChange={(e) => setFax(e.target.value)} />
        </div>
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        <button type="submit" disabled={busy}
          className="w-full py-3 bg-vida-700 text-white rounded-lg font-medium hover:bg-vida-800 disabled:opacity-50">
          {busy ? 'Guardando…' : 'Guardar mis datos'}
        </button>
      </form>
    </LeadPublicShell>
  );
}
